import "@/App.css";
import { useLayoutEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Mail, Phone } from "lucide-react";
import { SmoothScroll } from "@/components/SmoothScroll";
import { ScheduleProvider } from "@/components/ScheduleModal";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";

const NEXT_STEPS = [
  "Your application is reviewed personally.",
  "Qualified applicants will be invited to schedule an Introductory Conversation.",
  "If it's a mutual fit, we'll discuss licensing, training, contracting, and the opportunity to build your own independent insurance business.",
];

function ReceivedContent() {
  useLayoutEffect(() => {
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
    const toTop = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true, force: true });
    };
    toTop();
    // Run again after Lenis (parent effect) initializes on the next frames
    const r1 = requestAnimationFrame(toTop);
    const t1 = setTimeout(toTop, 60);
    return () => {
      cancelAnimationFrame(r1);
      clearTimeout(t1);
    };
  }, []);

  return (
    <main>
      {/* Hero / Confirmation */}
      <section data-testid="received-hero" className="relative min-h-[70vh] flex flex-col justify-center py-24 px-6 lg:px-10 max-w-[1400px] mx-auto pt-40">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 1 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-px w-10 bg-[#d4af37]" />
          <span className="mono text-[10px] uppercase tracking-[0.35em] text-[#d4af37]">Application Received</span>
        </motion.div>

        <Reveal>
          <span className="inline-flex h-16 w-16 rounded-full border border-[#d4af37]/30 items-center justify-center mb-10">
            <CheckCircle2 size={30} className="text-[#d4af37]" />
          </span>
        </Reveal>

        <Reveal delay={0.05}>
          <h1 className="serif font-light leading-[0.98] text-[#f4f0ea] text-[11vw] sm:text-[8vw] lg:text-[5.5vw] max-w-4xl">
            Thank You for <span className="italic text-[#d4af37]">Applying</span>
          </h1>
        </Reveal>

        <Reveal delay={0.2} className="mt-10 max-w-2xl space-y-6">
          <p className="text-[#f4f0ea]/90 text-lg leading-relaxed tracking-wide">
            We've successfully received your application.
          </p>
          <p className="text-[#a3a3a3] text-base leading-relaxed tracking-wide">
            John Campbell personally reviews every application. If your experience and goals appear to be a good mutual
            fit, we'll contact you within 1–2 business days to schedule a relaxed, no-pressure 30-minute Introductory
            Conversation.
          </p>
        </Reveal>
      </section>

      {/* What Happens Next */}
      <section data-testid="received-next" className="relative bg-[#030303] py-16 lg:py-24 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-3 mb-10">
            <span className="serif text-2xl lg:text-3xl text-[#f4f0ea]">What Happens Next</span>
            <span className="h-px flex-1 bg-white/10" />
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {NEXT_STEPS.map((step, i) => (
              <Reveal key={step} delay={i * 0.1}>
                <div
                  data-testid={`received-step-${i + 1}`}
                  className="relative h-full bg-gradient-to-b from-[#111] to-[#0a0a0a] border border-white/10 rounded-sm p-8 overflow-hidden"
                >
                  <div className="absolute top-0 left-0 right-0 h-px bg-[#d4af37]/40" />
                  <span className="h-11 w-11 rounded-full border border-[#d4af37]/30 flex items-center justify-center mb-6">
                    <CheckCircle2 size={18} className="text-[#d4af37]" />
                  </span>
                  <p className="text-[#a3a3a3] text-sm leading-relaxed tracking-wide">{step}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Questions + CTAs */}
      <section data-testid="received-questions" className="relative bg-[#0a0a0a] py-16 lg:py-24 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-[1fr_1fr] gap-12 lg:gap-16 items-start">
            <Reveal>
              <div className="flex items-center gap-3 mb-8">
                <span className="serif text-2xl lg:text-3xl text-[#f4f0ea]">Questions?</span>
                <span className="h-px flex-1 bg-white/10" />
              </div>
              <div className="space-y-6">
                <a
                  href="mailto:john@johncampbellinsurance.com"
                  data-testid="received-email"
                  className="group flex items-center gap-4"
                >
                  <span className="shrink-0 h-11 w-11 rounded-full border border-[#d4af37]/30 flex items-center justify-center">
                    <Mail size={17} className="text-[#d4af37]" />
                  </span>
                  <span className="flex flex-col leading-tight">
                    <span className="mono text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3]">Email</span>
                    <span className="text-[#f4f0ea]/90 text-base group-hover:text-[#d4af37] transition-colors duration-300">john@johncampbellinsurance.com</span>
                  </span>
                </a>
                <a
                  href="tel:+16234141766"
                  data-testid="received-phone"
                  className="group flex items-center gap-4"
                >
                  <span className="shrink-0 h-11 w-11 rounded-full border border-[#d4af37]/30 flex items-center justify-center">
                    <Phone size={17} className="text-[#d4af37]" />
                  </span>
                  <span className="flex flex-col leading-tight">
                    <span className="mono text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3]">Phone</span>
                    <span className="text-[#f4f0ea]/90 text-base group-hover:text-[#d4af37] transition-colors duration-300">(623) 414-1766</span>
                  </span>
                </a>
              </div>
            </Reveal>

            <Reveal delay={0.12}>
              <div className="bg-gradient-to-b from-[#111] to-[#0a0a0a] border border-[#d4af37]/20 rounded-sm p-8 lg:p-12 relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-px bg-[#d4af37]/40" />
                <h3 className="serif text-2xl lg:text-3xl text-[#f4f0ea] mb-4">
                  Explore while you <span className="italic text-[#d4af37]">wait</span>.
                </h3>
                <p className="text-[#a3a3a3] text-base leading-relaxed tracking-wide mb-10">
                  Learn more about who we are and the families we serve.
                </p>
                <div className="flex flex-col sm:flex-row gap-5">
                  <a
                    href="/"
                    data-testid="received-home-btn"
                    className="group inline-flex items-center gap-3 mono text-xs uppercase tracking-[0.2em] px-8 py-4 bg-[#f4f0ea] text-[#030303] rounded-full hover:bg-[#d4af37] transition-colors duration-400"
                  >
                    Return to Home
                    <ArrowRight size={15} className="transition-transform duration-400 group-hover:translate-x-1" />
                  </a>
                  <a
                    href="/#about"
                    data-testid="received-about-btn"
                    className="group inline-flex items-center gap-3 mono text-xs uppercase tracking-[0.2em] px-8 py-4 border border-[#d4af37]/50 text-[#d4af37] rounded-full hover:bg-[#d4af37] hover:text-[#030303] transition-colors duration-400"
                  >
                    Learn About John Campbell Insurance
                  </a>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}

export default function ApplicationReceived() {
  return (
    <div className="App grain bg-[#030303]">
      <ScheduleProvider>
        <SmoothScroll>
          <Navbar />
          <ReceivedContent />
          <Footer />
        </SmoothScroll>
      </ScheduleProvider>
    </div>
  );
}
